import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Card, Button, Alert, Pagination } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import Select from 'react-select';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  ativoManutencaoListar,
  obterOcorrenciasDoAtivo,
} from '~/Services/Service';
import FiltroData from './FiltroData';
import DBMemoTexto from './DBMemoTexto';

const TAMANHO_PAGINA = 20;

// W-0489 / T14 — Tab "Por Ativo": Select paginado on-scroll + grid + texto.
// Paridade Delphi UConsultaOcoAtivo.pas linhas 149-167 (selecao ativo via combo cdLAti
// pre-carregado uma vez no FormShow) e 350-367 (cdAtivo2AfterScroll).
// Web: lista inteira lazy-loaded — primeira pagina carrega ao abrir o menu,
// proximas paginas carregam conforme usuario scrolla. Busca server-side com debounce.

const DEBOUNCE_MS = 300;
const TAMANHO_PAGINA_BUSCA = 50;
const MENSAGEM_CARREGANDO = 'Carregando...';

const extrairMsg = (err, fallback) =>
  (err && err.message) ? err.message : fallback;

const toOption = (a) => {
  if (!a || a.codigo_ativo == null) return null;
  return {
    value: a.codigo_ativo,
    label: `${a.codigo_ativo} - ${a.nome || ''}`,
    codigo_ativo: a.codigo_ativo,
    nome: a.nome,
  };
};

const formatarDataHora = (iso) =>
  iso ? moment(iso).format('DD/MM/YYYY HH:mm:ss') : '';

const TabPorAtivo = () => {
  const [dataInicio, setDataInicio] = useState(null);
  const [ativoSelecionado, setAtivoSelecionado] = useState(null);
  const [ativoConsultado, setAtivoConsultado] = useState(null);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [ocorrenciaSelecionada, setOcorrenciaSelecionada] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erroConsulta, setErroConsulta] = useState(null);
  // Q-02 A — sort default: titulo ASC (paridade Delphi vord=0).
  const [ordenacao, setOrdenacao] = useState({ coluna: 'titulo', direcao: 'asc' });
  const [paginaAtual, setPaginaAtual] = useState(1);

  const debounceTimerRef = useRef(null);
  const ultimaBuscaIdRef = useRef(0);
  const ultimaConsultaIdRef = useRef(0);
  // Anti-loop: carrega lista inicial uma unica vez (mesmo se backend retornar [] legitimo).
  const carregouInicialRef = useRef(false);

  // Estado do Select paginado on-scroll (sem MIN_CHARS — carrega lista inteira sob demanda).
  const [opcoesAtivos, setOpcoesAtivos] = useState([]);
  const [buscaAtivo, setBuscaAtivo] = useState('');
  const [paginaBusca, setPaginaBusca] = useState(1);
  const [totalPaginasBusca, setTotalPaginasBusca] = useState(1);
  const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);

  const fetchAtivos = (pagina, busca, modo) => {
    const buscaId = ultimaBuscaIdRef.current + 1;
    ultimaBuscaIdRef.current = buscaId;
    setCarregandoOpcoes(true);
    ativoManutencaoListar({
      pagina,
      tamanho_pagina: TAMANHO_PAGINA_BUSCA,
      busca: busca || undefined,
    })
      .then((resp) => {
        if (buscaId !== ultimaBuscaIdRef.current) return;
        const itens = resp && Array.isArray(resp.itens) ? resp.itens : [];
        const totalPags = resp && resp.total_paginas ? resp.total_paginas : 1;
        const novasOpcoes = itens.map(toOption).filter(Boolean);
        setOpcoesAtivos((prev) =>
          modo === 'append' ? [...prev, ...novasOpcoes] : novasOpcoes
        );
        setTotalPaginasBusca(totalPags);
        setCarregandoOpcoes(false);
      })
      .catch(() => {
        if (buscaId !== ultimaBuscaIdRef.current) return;
        setCarregandoOpcoes(false);
      });
  };

  // Carga inicial ao abrir o menu pela primeira vez. Ref impede re-fetch se backend
  // retornar [] legitimamente e usuario reabrir o menu.
  useEffect(() => {
    if (menuAberto && !carregouInicialRef.current && !carregandoOpcoes) {
      carregouInicialRef.current = true;
      fetchAtivos(1, '', 'reset');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuAberto]);

  const handleInputChange = (valor, { action }) => {
    // Apenas reage a digitacao do usuario (ignora set-value/menu-close).
    if (action !== 'input-change') return;
    setBuscaAtivo(valor);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setPaginaBusca(1);
      fetchAtivos(1, valor, 'reset');
    }, DEBOUNCE_MS);
  };

  const handleMenuScrollToBottom = () => {
    if (carregandoOpcoes) return;
    if (paginaBusca >= totalPaginasBusca) return;
    const proxima = paginaBusca + 1;
    setPaginaBusca(proxima);
    fetchAtivos(proxima, buscaAtivo, 'append');
  };

  const colunas = useMemo(
    () => [
      {
        title: 'Título',
        key: 'titulo',
        ordenacao: true,
        align: 'left',
      },
      {
        title: 'Data/Hora',
        key: '_dataFmt',
        ordenacao: true,
        align: 'left',
        width: '200px',
      },
    ],
    []
  );

  // Filtro de data client-side (paridade Delphi R5 modo Por Ativo: Q5 NAO aplica MaskEdit1).
  // Backend retorna todas as ocorrencias; frontend filtra localmente quando usuario preenche data.
  // Usa moment.isSameOrAfter (timezone-safe) em vez de comparacao string ISO — se o backend
  // serializar sem 'Z' ou em timezone diferente, comparacao string-a-string falha em
  // fronteiras de dia (LICOES W-0199).
  const dadosFiltrados = useMemo(() => {
    if (!dataInicio) return ocorrencias;
    const corte = moment(dataInicio).startOf('day');
    return ocorrencias.filter(
      (o) => o.data_ocorrencia && moment(o.data_ocorrencia).isSameOrAfter(corte)
    );
  }, [ocorrencias, dataInicio]);

  const dadosOrdenados = useMemo(() => {
    if (!ordenacao.coluna) return dadosFiltrados;
    const colKey =
      ordenacao.coluna === '_dataFmt' ? 'data_ocorrencia' : ordenacao.coluna;
    return [...dadosFiltrados].sort((a, b) => {
      const va = a[colKey] != null ? a[colKey] : '';
      const vb = b[colKey] != null ? b[colKey] : '';
      const strA = String(va).toLowerCase();
      const strB = String(vb).toLowerCase();
      if (strA < strB) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (strA > strB) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
  }, [dadosFiltrados, ordenacao]);

  // Paginacao client-side (GAP-A2-FE / Gate 3 Q-03 D tier B).
  const totalPaginas = Math.max(1, Math.ceil(dadosOrdenados.length / TAMANHO_PAGINA));
  const paginaCorrigida = Math.min(paginaAtual, totalPaginas);
  const inicioPagina = (paginaCorrigida - 1) * TAMANHO_PAGINA;

  const dadosFormatados = useMemo(
    () =>
      dadosOrdenados
        .slice(inicioPagina, inicioPagina + TAMANHO_PAGINA)
        .map((o) => ({
          ...o,
          _dataFmt: formatarDataHora(o.data_ocorrencia),
          _key: `${o.codigo_ativo}-${o.data_ocorrencia}-${o.titulo || ''}`,
        })),
    [dadosOrdenados, inicioPagina]
  );

  const handleConsultar = () => {
    if (ativoSelecionado == null) return;
    const ativoAtual = ativoSelecionado;
    const consultaId = ultimaConsultaIdRef.current + 1;
    ultimaConsultaIdRef.current = consultaId;

    setCarregando(true);
    setErroConsulta(null);
    setOcorrenciaSelecionada(null);
    setPaginaAtual(1);
    // Paridade Delphi R5: modo Por Ativo (Q5) NAO envia dataInicio ao backend.
    // Filtro de data e aplicado client-side (dadosFiltrados acima).
    obterOcorrenciasDoAtivo(ativoAtual.codigo_ativo)
      .then((res) => {
        if (consultaId !== ultimaConsultaIdRef.current) return;
        const lista = Array.isArray(res) ? res : [];
        setOcorrencias(lista);
        setAtivoConsultado(ativoAtual);
        setCarregando(false);
      })
      .catch((err) => {
        if (consultaId !== ultimaConsultaIdRef.current) return;
        const msg = extrairMsg(err, 'Erro ao carregar ocorrências do ativo.');
        setErroConsulta(msg);
        setOcorrencias([]);
        setAtivoConsultado(null);
        setCarregando(false);
        toast.error(msg);
      });
  };

  const textoSelecionado =
    ocorrenciaSelecionada != null
      ? ocorrenciaSelecionada.texto || ''
      : '';

  return (
    <div className="p-2 p-md-3">
      <Row className="mb-3">
        <Col xs={12} md={6} lg={5} className="mb-2 mb-md-0">
          <label className="small mb-1 d-block">Ativo</label>
          <Select
            options={opcoesAtivos}
            value={ativoSelecionado}
            onChange={(opt) => setAtivoSelecionado(opt)}
            onInputChange={handleInputChange}
            onMenuOpen={() => setMenuAberto(true)}
            onMenuClose={() => setMenuAberto(false)}
            onMenuScrollToBottom={handleMenuScrollToBottom}
            isLoading={carregandoOpcoes}
            inputValue={buscaAtivo}
            placeholder="Selecione um ativo..."
            noOptionsMessage={() =>
              carregandoOpcoes ? MENSAGEM_CARREGANDO : 'Nenhum ativo encontrado'
            }
            loadingMessage={() => MENSAGEM_CARREGANDO}
            isClearable
            filterOption={null}
            styles={customStyles}
            components={{ DropdownIndicator }}
          />
        </Col>
        <Col xs={12} sm={6} md={3} lg={3} className="mb-2 mb-md-0">
          <FiltroData value={dataInicio} onChange={setDataInicio} />
        </Col>
        <Col
          xs={12}
          md={3}
          lg={4}
          className="d-flex align-items-end justify-content-md-start"
        >
          <Button
            variant="primary"
            onClick={handleConsultar}
            disabled={ativoSelecionado == null || carregando}
          >
            {carregando ? 'Consultando...' : 'Consultar'}
          </Button>
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={7} className="mb-3">
          <Card>
            <Card.Header className="py-2">
              <strong>Ocorrências</strong>
              {ativoConsultado && (
                <span className="ml-2 small text-muted">
                  Ativo {ativoConsultado.codigo_ativo} —{' '}
                  {ativoConsultado.nome || ''}
                </span>
              )}
            </Card.Header>
            <Card.Body
              className="p-0"
              style={{ maxHeight: 360, overflow: 'auto' }}
            >
              {erroConsulta ? (
                <Alert variant="danger" className="m-2 mb-0">
                  {erroConsulta}
                </Alert>
              ) : carregando ? (
                <Loading />
              ) : ativoConsultado == null ? (
                <div className="text-muted p-3 small">
                  Selecione um ativo e clique em "Consultar".
                </div>
              ) : ocorrencias.length === 0 ? (
                <div className="text-muted p-3 small">
                  Nenhuma ocorrência registrada para este ativo.
                </div>
              ) : (
                <>
                  <TabelaDinamica
                    colunas={colunas}
                    dados={dadosFormatados}
                    striped
                    hover
                    ordenacao={ordenacao}
                    onOrdenacaoChange={(o) => {
                      setOrdenacao(o);
                      setPaginaAtual(1);
                    }}
                    cliqueLinha={(item) => setOcorrenciaSelecionada(item)}
                    linhaSelecionada={(item) =>
                      ocorrenciaSelecionada != null &&
                      item._key === ocorrenciaSelecionada._key
                    }
                  />
                  {totalPaginas > 1 && (
                    <div className="d-flex justify-content-between align-items-center px-2 py-2 small">
                      <span className="text-muted">
                        Mostrando {inicioPagina + 1}–
                        {Math.min(inicioPagina + TAMANHO_PAGINA, dadosOrdenados.length)} de{' '}
                        {dadosOrdenados.length}
                      </span>
                      <Pagination className="mb-0">
                        <Pagination.First
                          onClick={() => setPaginaAtual(1)}
                          disabled={paginaCorrigida === 1}
                        />
                        <Pagination.Prev
                          onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                          disabled={paginaCorrigida === 1}
                        />
                        <Pagination.Item active>{paginaCorrigida}</Pagination.Item>
                        <Pagination.Next
                          onClick={() =>
                            setPaginaAtual((p) => Math.min(totalPaginas, p + 1))
                          }
                          disabled={paginaCorrigida === totalPaginas}
                        />
                        <Pagination.Last
                          onClick={() => setPaginaAtual(totalPaginas)}
                          disabled={paginaCorrigida === totalPaginas}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={5} className="mb-3">
          <Card>
            <Card.Header className="py-2">
              <strong>Texto da Ocorrência</strong>
            </Card.Header>
            <Card.Body>
              <DBMemoTexto
                texto={textoSelecionado}
                placeholder="Selecione uma ocorrência na lista para visualizar o texto."
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TabPorAtivo;
